import React, { useEffect, useRef, useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Button,
  Input,
  FormGroup,
  Label,
  Nav,
  NavItem,
  NavLink,
  TabPane,
  InputGroupText,
  InputGroupAddon,
  InputGroup,
  TabContent,
  Badge,
} from "reactstrap";
import Select from "react-select";
import { customStyle } from "components/Common/constants";
import { showAlert } from "components/Common/commonFunctions";
import Breadcrumbs from "components/Common/BreadCrumbs";
import { getSparePartByID, editSparePart } from "actions/sparePart";
import { useDispatch, useSelector } from "react-redux";
import { getManufacturerList } from "actions/manufacturer";
import NotificationAlert from "react-notification-alert";
import { REGEX } from "components/Common/constants";
import { urlDecodedId } from "components/Common/encodeDecodeFunction";

const initialFields = {
  itemNumber: { value: "", error: "" },
  manufacturer: { value: "", error: "" },
  price: { value: "", error: "" },
  quantity: { value: "", error: "" },
  description: { value: "", error: "" },
  status: { value: "", error: "" },
};

const SparePartDetails = () => {
  const { id: encodedId } = useParams();
  const decodedId = urlDecodedId(encodedId);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const notificationAlertRef = useRef(null);

  const manufacturerList = useSelector(
    (state) => state.manufacturer.manufacturerList,
  );
  // parse ?edit=true like UserDetails
  const queryParams = new URLSearchParams(location.search);
  const isEditFromUrl = queryParams.get("edit") === "true";

  const [sparePart, setSparePart] = useState(null);
  const [sparePartFormData, setSparePartFormData] = useState(initialFields);
  const [showAlternative, setShowAlternative] = useState({
    loader: false,
    error: "",
  });
  const [isEditing, setIsEditing] = useState(isEditFromUrl);
  const [activeTab, setActiveTab] = useState("DETAILS");
  const [showLoaderOnClick, setShowLoaderOnClick] = useState(false);

  useEffect(() => {
    const filterJSON = [
      {
        field: "Status",
        condition: "eq",
        filterValues: ["ACTIVE"],
      },
    ];
    getManufacturerList(dispatch, { filters: JSON.stringify(filterJSON) });
  }, [dispatch]);

  useEffect(() => {
    if (decodedId) {
      getSparePartDetails(decodedId);
    }
  }, [decodedId]);

  const notify = (type, message) => {
    const options = {
      place: "tr",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    };
    notificationAlertRef.current?.notificationAlert(options);
  };

  const getSparePartDetails = async (spareId) => {
    try {
      setShowAlternative({ loader: true, error: false });
      const res = await getSparePartByID(spareId);

      if (res.code === 200) {
        const sparePartDetailsRes = res?.data;
        setSparePart(sparePartDetailsRes);
        setSparePartFormData({
          itemNumber: {
            value: sparePartDetailsRes.itemNumber || "",
            error: "",
          },
          manufacturer: {
            value: sparePartDetailsRes.manufacturer || "",
            error: "",
          },
          price: { value: sparePartDetailsRes.price || "", error: "" },
          quantity: { value: sparePartDetailsRes.quantity || "", error: "" },
          description: {
            value: sparePartDetailsRes.description || "",
            error: "",
          },
          status: {
            value: sparePartDetailsRes.status || "",
            error: ""
          }
        });
        setShowAlternative({ loader: false, error: false });
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Failed to load spare part details!";
        setShowAlternative({ loader: false, error: message });
      }
    } catch (error) {
      setShowAlternative({
        loader: false,
        error: error || "Failed to load spare part details!",
      });
    }
  };

  const toggleTab = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const handleInputChange = (fieldName, newValue) => {
    setSparePartFormData((prev) => ({
      ...prev,
      [fieldName]: {
        value: newValue,
        error: "",
      },
    }));
  };

  const getValidationError = (name, value) => {
    const isEmpty = value === "" || value === null || value === undefined;

    switch (name) {
      case "itemNumber":
        return isEmpty ? "Please enter a itemNumber" : "";

      case "manufacturer":
        return !value ? "Please select a manufacturer" : "";

      case "price":
        if (isEmpty) return "Please enter a price";
        if (Number(value) <= 0) return "Enter number greater than 0";

        // it allow integers or decimals
        return REGEX.INT_DEC_REGEX.test(value) ? "" : "Enter valid price";

      case "quantity":
        if (isEmpty) return "Please enter a quantity";
        if (Number(value) <= 0) return "Enter number greater than 0";

        // it allow only whole numbers and no decimal
        return REGEX.WHOLE_NUM_REGEX.test(value) ? "" : "Enter valid quantity";

      case "description":
        return isEmpty ? "Please enter a description" : "";

      default:
        return "";
    }
  };

  const validateForm = () => {
    let newForm = { ...sparePartFormData };
    Object.keys(newForm).forEach((key) => {
      newForm[key] = {
        ...newForm[key],
        error: getValidationError(key, newForm[key].value),
      };
    });
    setSparePartFormData(newForm);
    return Object.values(newForm).every((item) => item.error === "");
  };

  const handleOnBlur = (name, value) => {
    const trimmed = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmed);
    setSparePartFormData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmed,
        error,
      },
    }));
  };

  const handleChargesChange = (e) => {
    const raw = e.target.value;
    if (raw === "" || REGEX.ONLY_NUMBERS_REGEX.test(raw)) {
      const normalized = raw.startsWith(".") ? `0${raw}` : raw;
      setSparePartFormData((prev) => ({
        ...prev,
        price: { ...prev.price, value: normalized, error: "" },
      }));
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSparePartFormData({
      itemNumber: { value: sparePart?.itemNumber || "", error: "" },
      manufacturer: { value: sparePart?.manufacturer || "", error: "" },
      price: { value: sparePart?.price || "", error: "" },
      quantity: { value: sparePart?.quantity || "", error: "" },
      description: { value: sparePart?.description || "", error: "" },
    });
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete("edit");
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  };

  const handleOnEditClick = () => {
    setIsEditing(true);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("edit", "true");
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  };

  const handleSaveClick = async () => {
    if (validateForm()) {
      try {
        setShowLoaderOnClick(true);
        const bodyData = {
          itemNumber: sparePartFormData.itemNumber.value,
          manufacturerID: sparePartFormData.manufacturer.value?.ID,
          price: Number(sparePartFormData.price.value),
          quantity: Number(sparePartFormData.quantity.value),
          description: sparePartFormData.description.value,
        };
        const res = await editSparePart(decodedId, bodyData);
        if (res.code === 200) {
          notify("success", res.message || "Spare part updated successfully");
          await getSparePartDetails(decodedId);
          setIsEditing(false);
          const searchParams = new URLSearchParams(location.search);
          searchParams.delete("edit");
          navigate(`${location.pathname}?${searchParams.toString()}`, {
            replace: true,
          });
        } else {
          const message = Array.isArray(res?.message)
            ? res?.message[0]?.Msg
            : res?.message || "Failed to update spare part!";
          notify("danger", message);
        }
      } catch (e) {
        notify("danger", "Failed to update spare part");
      } finally {
        setShowLoaderOnClick(false);
      }
    }
  };

  const handleChangeStatus = async (newStatus) => {
    try {
      setShowLoaderOnClick(true);
      const res = await editSparePart(sparePart.ID, { status: newStatus });
      if (res && res.code === 200) {
        notify("success", res.message || "Status updated");
        await getSparePartDetails(sparePart.ID);
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Failed to update spare part!";
        notify("danger", message);
      }
    } catch (err) {
      notify("danger", "Failed to update status");
    } finally {
      setShowLoaderOnClick(false);
    }
  };

  const handleStatusChangeModal = () => {
    showAlert({
      title: `Are you sure you want to ${sparePart?.status === "ACTIVE" ? "inactivate" : "activate"
        } this spare part?`,
      message: `Spare Part "${sparePart?.itemNumber || ""}" will be ${sparePart?.status === "ACTIVE" ? "inactive" : "active"
        }.`,
      type: "warning",
      confirmStyle: "default",
      cancelStyle: "secondary",
      confirmText: "Yes",
      cancelText: "Cancel",
      onConfirm: () => {
        handleChangeStatus(
          sparePart?.status === "ACTIVE" ? "INACTIVE" : "ACTIVE",
        );
      },
      onCancel: () => { },
    });
  };

  return (
    <Container fluid className="mt-4">
      <div className="rna-container">
        <NotificationAlert ref={notificationAlertRef} />
      </div>

      <Breadcrumbs
        items={[
          { name: "Spare Parts", link: "/admin/spare-parts" },
          { name: `${sparePartFormData.itemNumber.value || "Details"}` },
        ]}
      />

      <Row>
        <Card className="m-3 shadow-sm border-0">
          {showAlternative.loader ? (
            <div className="loaderContainer">
              <i className="fa fa-spinner fa-spin fa-2xl loader" />
            </div>
          ) : showAlternative.error ? (
            <div className="loaderContainer text-muted d-flex flex-column">
              <h4 className="text-muted">No Spare Part Data Available</h4>
              <span className="text-danger">{showAlternative.error}</span>
            </div>
          ) : (
            <>
              <Nav className="custom-tabs">
                <NavItem>
                  <NavLink
                    className={`text-center ${activeTab === "DETAILS" ? "active" : ""
                      }`}
                    href="#"
                    onClick={() => toggleTab("DETAILS")}
                  >
                    Spare Part Details
                  </NavLink>
                </NavItem>
              </Nav>

              <CardBody>
                <TabContent activeTab={activeTab}>
                  <TabPane tabId="DETAILS">
                    <Row className="justify-content-center">
                      <Col md={12} lg={12}>
                        <Row className="d-flex justify-content-end mr-2 mb-1">
                          {!isEditing && (
                            <>
                              <Button
                                color="default"
                                size="sm"
                                onClick={handleOnEditClick}
                              >
                                <i className="fa fa-pencil mr-2" />
                                Edit
                              </Button>
                              <Button
                                className="ml-3"
                                color="secondary"
                                size="sm"
                                onClick={() => handleStatusChangeModal()}
                                disabled={!sparePart}
                              >
                                {sparePart?.status === "ACTIVE"
                                  ? "Inactivate"
                                  : sparePart?.status === "INACTIVE"
                                    ? "Activate"
                                    : ""}
                              </Button>
                            </>
                          )}
                        </Row>

                        <Row className="px-3 mt-2">
                          <Col md={4} className="mb-2">
                            <FormGroup
                              className={`has-label ${sparePartFormData.itemNumber.error
                                ? "has-danger"
                                : ""
                                } d-flex flex-column align-items-start`}
                            >
                              <Label>
                                Item Number
                                {isEditing && <span className="validation-error">*</span>}
                              </Label>
                              {isEditing ? (
                                <>
                                  <Input
                                    name="itemNumber"
                                    type="text"
                                    maxLength={100}
                                    placeholder="Enter item number"
                                    value={sparePartFormData.itemNumber.value}
                                    onChange={(e) =>
                                      handleInputChange(
                                        e.target.name,
                                        e.target.value,
                                      )
                                    }
                                    onBlur={(e) =>
                                      handleOnBlur(
                                        e.target.name,
                                        e.target.value,
                                      )
                                    }
                                  />
                                  {sparePartFormData.itemNumber.error && (
                                    <small className="validation-error">
                                      {sparePartFormData.itemNumber.error}
                                    </small>
                                  )}
                                </>
                              ) : (
                                <span className="small-text">
                                  {sparePartFormData.itemNumber.value || "-"}
                                </span>
                              )}
                            </FormGroup>
                          </Col>

                          <Col md={4} className="mb-2">
                            <FormGroup
                              className={`has-label ${sparePartFormData.manufacturer.error
                                ? "has-danger"
                                : ""
                                } d-flex flex-column align-items-start`}
                            >
                              <Label>
                                Manufacturer{" "}
                                {isEditing && <span className="validation-error">*</span>}
                              </Label>

                              {isEditing ? (
                                <>
                                  <Select
                                    className={`text-left ${sparePartFormData.manufacturer.error
                                      ? "is-invalid"
                                      : ""
                                      }`}
                                    classNamePrefix="react-select"
                                    options={
                                      manufacturerList?.data?.manufacturer || []
                                    }
                                    placeholder="Select Manufacturer"
                                    styles={customStyle}
                                    value={sparePartFormData.manufacturer.value}
                                    getOptionLabel={(option) => option.name}
                                    getOptionValue={(option) => option.ID}
                                    onChange={(selected) =>
                                      handleInputChange(
                                        "manufacturer",
                                        selected,
                                      )
                                    }
                                    onBlur={() =>
                                      handleOnBlur(
                                        "manufacturer",
                                        sparePartFormData.manufacturer.value,
                                      )
                                    }
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                  />
                                  {sparePartFormData.manufacturer.error && (
                                    <small className="validation-error">
                                      {sparePartFormData.manufacturer.error}
                                    </small>
                                  )}
                                </>
                              ) : (
                                <span className="small-text">
                                  {sparePartFormData.manufacturer.value?.name ||
                                    "-"}
                                </span>
                              )}
                            </FormGroup>
                          </Col>

                          <Col md={4} className="mb-2">
                            <FormGroup
                              className={`has-label ${sparePartFormData.price.error
                                ? "has-danger"
                                : ""
                                } d-flex flex-column align-items-start`}
                            >
                              <Label>
                                Unit Price{" "}
                                {isEditing && <span className="validation-error">*</span>}
                              </Label>
                              {isEditing ? (
                                <>
                                  <InputGroup className="input-group">
                                    <InputGroupAddon addonType="prepend">
                                      <InputGroupText className="custom-border d-flex align-items-center text-muted">
                                        SAR
                                      </InputGroupText>
                                    </InputGroupAddon>
                                    <Input
                                      name="price"
                                      type="text"
                                      maxLength={7}
                                      placeholder="Enter price"
                                      value={sparePartFormData.price.value}
                                      onChange={handleChargesChange}
                                      onBlur={(e) =>
                                        handleOnBlur(
                                          e.target.name,
                                          e.target.value,
                                        )
                                      }
                                    />
                                  </InputGroup>
                                  {sparePartFormData.price.error && (
                                    <small className="validation-error">
                                      {sparePartFormData.price.error}
                                    </small>
                                  )}
                                </>
                              ) : (
                                <span className="small-text">
                                  <span className="mr-1">SAR</span>
                                  {sparePartFormData.price.value ?? "-"}
                                </span>
                              )}
                            </FormGroup>
                          </Col>

                          <Col md={4} className="mb-2">
                            <FormGroup
                              className={`has-label ${sparePartFormData.quantity.error
                                ? "has-danger"
                                : ""
                                } d-flex flex-column align-items-start`}
                            >
                              <Label>
                                Quantity {isEditing && <span className="validation-error">*</span>}
                              </Label>
                              {isEditing ? (
                                <>
                                  <Input
                                    name="quantity"
                                    type="text"
                                    min={1}
                                    maxLength={9}
                                    placeholder="Enter quantity"
                                    value={sparePartFormData.quantity.value}
                                    onChange={(e) =>
                                      handleInputChange(
                                        e.target.name,
                                        e.target.value,
                                      )
                                    }
                                    onBlur={(e) =>
                                      handleOnBlur(
                                        e.target.name,
                                        e.target.value,
                                      )
                                    }
                                  />
                                  {sparePartFormData.quantity.error && (
                                    <small className="validation-error">
                                      {sparePartFormData.quantity.error}
                                    </small>
                                  )}
                                </>
                              ) : (
                                <span className="small-text">
                                  {sparePartFormData.quantity.value ?? "-"}
                                </span>
                              )}
                            </FormGroup>
                          </Col>

                          <Col md={4} className="mb-2">
                            <FormGroup
                              className={`has-label ${sparePartFormData.description.error
                                ? "has-danger"
                                : ""
                                } d-flex flex-column align-items-start`}
                            >
                              <Label>
                                Description
                                {isEditing && <span className="validation-error">*</span>}
                              </Label>
                              {isEditing ? (
                                <>
                                  <Input
                                    name="description"
                                    type="textarea"
                                    cols="80"
                                    rows="4"
                                    maxLength={500}
                                    placeholder="Enter description"
                                    value={sparePartFormData.description.value}
                                    onChange={(e) =>
                                      handleInputChange(
                                        e.target.name,
                                        e.target.value,
                                      )
                                    }
                                    onBlur={(e) =>
                                      handleOnBlur(
                                        e.target.name,
                                        e.target.value,
                                      )
                                    }
                                  />
                                  {sparePartFormData.description.error && (
                                    <small className="validation-error">
                                      {sparePartFormData.description.error}
                                    </small>
                                  )}
                                </>
                              ) : (
                                <span className="small-text">
                                  {sparePartFormData.description.value ?? "-"}
                                </span>
                              )}
                            </FormGroup>
                          </Col>
                          <Col md={4} className="mb-0">
                            <FormGroup className="d-flex flex-column align-items-start mt-2">
                              <Label>Status</Label>
                              <span>
                                <Badge
                                  className="px-3 py-2"
                                  color={
                                    sparePartFormData?.status?.value === "ACTIVE"
                                      ? "success"
                                      : "warning"
                                  }
                                  pill
                                >
                                  {sparePartFormData?.status?.value}
                                </Badge>
                              </span>
                            </FormGroup>
                          </Col>
                        </Row>

                        <Row>
                          <Col className="d-flex justify-content-end">
                            {isEditing && (
                              <>
                                <Button
                                  className="mr-2"
                                  color="secondary"
                                  size="sm"
                                  onClick={handleCancel}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  color="default"
                                  size="sm"
                                  onClick={handleSaveClick}
                                  disabled={
                                    Object.values(sparePartFormData).some(
                                      (item) => item.error !== "",
                                    ) || showLoaderOnClick
                                  }
                                >
                                  {showLoaderOnClick ? (
                                    <i className="fa fa-spinner fa-spin fa-lg text-white" />
                                  ) : (
                                    "Save"
                                  )}
                                </Button>
                              </>
                            )}
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </TabPane>
                </TabContent>
              </CardBody>
            </>
          )}
        </Card>
      </Row>
    </Container>
  );
};

export default SparePartDetails;
